import React from 'react';
import { Card, CardContent } from '../../../../components/card';
import { CouponPerformanceRow } from '../MerchantHeatmap.data';

const PERFORMANCE_BADGE = {
    excellent: { bg: '#D1FAE5', text: '#065F46', label: 'Excellent' },
    good: { bg: '#BEDBFF', text: '#1C398E', label: 'Good' },
    normal: { bg: '#FFD6A7', text: '#7E2A0C', label: 'Normal' },
    not_good: { bg: '#FFC9C9', text: '#82181A', label: 'Not Good' },
    na: { bg: 'transparent', text: '#364153', label: 'N/A' },
} as const;

type PerformanceLevel = keyof typeof PERFORMANCE_BADGE;

const getPerformanceFromConversionRate = (conversionRate: number | null): PerformanceLevel => {
    if (conversionRate === null || conversionRate === undefined) return 'na';
    if (conversionRate >= 80) return 'excellent';
    if (conversionRate >= 60) return 'good';
    if (conversionRate >= 40) return 'normal';
    return 'not_good';
};

interface CouponPerformanceTableProps {
    rows: CouponPerformanceRow[];
}

export const CouponPerformanceTable: React.FC<CouponPerformanceTableProps> = ({ rows }) => {
    return (
        <Card className="bg-white dark:bg-gray-800 border border-[#e6e6e6] dark:border-gray-700 rounded-[12.13px]">
            <CardContent className="p-0">
                <div className="px-[19.5px] pt-[19.5px]">
                    <div className="text-[15px] font-semibold text-[#364153] dark:text-white leading-[13px]" style={{ fontFamily: 'Nunito' }}>
                        Coupon Performance Comparison
                    </div>
                </div>

                <div className="mt-[19.49px] overflow-auto">
                    <div className="min-w-[900px]">
                        {/* Header */}
                        <div className="h-[32.5px] bg-[#F9FAFB] dark:bg-gray-700 border-b border-[rgba(0,0,0,0.10)] dark:border-gray-600 flex items-center px-[15px]">
                            <div className="flex-1 px-[6.5px] text-[11.4px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Coupon
                            </div>
                            <div className="w-[113.23px] px-[6.5px] text-center text-[11.4px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Views
                            </div>
                            <div className="w-[169.24px] px-[6.5px] text-center text-[11.4px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Redeemed (in-app)
                            </div>
                            <div className="w-[169.24px] px-[6.5px] text-center text-[11.4px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Usage (in-store)
                            </div>
                            <div className="w-[144.09px] px-[6.5px] text-center text-[11.4px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Conv. Rate
                            </div>
                            <div className="w-[114.19px] px-[6.5px] text-center text-[11.4px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Performance
                            </div>
                        </div>

                        {/* Rows */}
                        {rows.map((row, idx) => {
                            const performanceLevel = getPerformanceFromConversionRate(row.conversion_rate);
                            const badge = PERFORMANCE_BADGE[performanceLevel];
                            const conv = row.conversion_rate == null ? 'N/A' : `${Math.round(row.conversion_rate)}%`;
                            return (
                                <div
                                    key={`${row.coupon_name}-${idx}`}
                                    className="h-[39.8px] border-b border-[rgba(0,0,0,0.10)] dark:border-gray-600 flex items-center px-[15px]"
                                >
                                    <div className="flex-1 px-[6.5px] text-[11.4px] font-medium text-[#4A5565] dark:text-gray-300 leading-[16.25px] truncate" style={{ fontFamily: 'Nunito' }}>
                                        {row.coupon_name}
                                    </div>
                                    <div className="w-[113.23px] px-[6.5px] text-center text-[11.4px] font-medium text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                        {row.views}
                                    </div>
                                    <div className="w-[169.24px] px-[6.5px] text-center text-[11.4px] font-medium text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                        {row.redeemed_in_app}
                                    </div>
                                    <div className="w-[169.24px] px-[6.5px] text-center text-[11.4px] font-medium text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                        {row.usage_in_store}
                                    </div>
                                    <div className="w-[144.09px] px-[6.5px] text-center text-[11.4px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                        {conv}
                                    </div>
                                    <div className="w-[114.19px] px-[6.5px] flex justify-center">
                                        <span
                                            className="px-[6px] py-[2px] rounded-full text-[11px] font-medium leading-[16px]"
                                            style={{
                                                backgroundColor: badge.bg,
                                                color: badge.text,
                                                fontFamily: 'Nunito',
                                            }}
                                        >
                                            {badge.label}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

